import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Trophy, ArrowRight } from "lucide-react";
import { FadeUp, MaskedLines } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FoundingHall() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/founding-keepers`).then((r) => setData(r.data)).catch(() => setData({ keepers: [], total: 100, claimed: 0 }));
  }, []);

  const remaining = data ? Math.max(data.total - data.claimed, 0) : null;

  return (
    <>
      <section className="bg-forest-deep text-bone relative overflow-hidden" data-testid="hall-hero">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[40rem] h-[40rem] rounded-full bg-honey/10 blur-3xl pointer-events-none" />
        <div className="max-w-5xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-16 text-center relative">
          <FadeUp>
            <Trophy className="w-10 h-10 text-honey mx-auto mb-6" strokeWidth={1.5} />
          </FadeUp>
          <h1 className="font-display font-medium tracking-tighter leading-[0.98] text-[clamp(2.6rem,6.5vw,5.5rem)]" data-testid="hall-heading">
            <MaskedLines
              lines={[
                <span key="a">The Founding</span>,
                <span key="b"><em className="text-honey not-italic font-semibold">Keepers Hall</em></span>,
              ]}
              delay={0.2}
            />
          </h1>
          <FadeUp delay={0.7} className="mt-6 max-w-xl mx-auto">
            <p className="text-bone/60 leading-relaxed">
              The first one hundred beekeepers who believed. Founding Keepers lock in
              their rate forever — and, if they choose, their name lives here.
            </p>
            {remaining !== null && (
              <p className="inline-block mt-6 bg-honey/15 border border-honey/40 text-honey text-sm font-semibold px-5 py-2 rounded-full" data-testid="hall-spots-counter">
                {remaining} of {data.total} spots remaining
              </p>
            )}
          </FadeUp>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24" data-testid="hall-names-section">
        {!data ? (
          <p className="text-center text-ink/40 py-10">Loading...</p>
        ) : data.keepers.length === 0 ? (
          <FadeUp className="text-center py-10" >
            <p className="font-display text-3xl text-forest mb-4" data-testid="hall-empty-state">The first names are on their way.</p>
            <p className="text-ink/50 max-w-md mx-auto mb-10">
              Founding Keepers appear here as spots are claimed. Yours could be the
              very first name in the hall.
            </p>
          </FadeUp>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="hall-names-grid">
            {data.keepers.map((k, i) => (
              <FadeUp key={`${k.first_name}-${i}`} delay={(i % 3) * 0.06}>
                <div
                  className="bg-white border border-honey/50 rounded-2xl px-6 py-6 text-center transition-colors duration-300 hover:border-honey"
                  data-testid={`hall-keeper-${i + 1}`}
                >
                  <p className="font-display text-2xl font-medium text-forest">{k.first_name}</p>
                  <p className="text-xs uppercase tracking-[0.2em] text-honey-dark mt-1.5">
                    Founding Keeper № {i + 1}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
        )}

        {remaining !== null && remaining > 0 && (
          <FadeUp delay={0.2} className="text-center mt-16">
            <Link
              to="/pre-register?src=hall"
              data-testid="hall-cta"
              className="group inline-flex items-center gap-2 bg-forest text-bone font-semibold px-10 py-4 rounded-full transition-colors duration-300 hover:bg-honey hover:text-forest"
            >
              Claim Your Founding Keeper Spot
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        )}
      </section>
    </>
  );
}
